import { useState, ReactNode } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight } from "lucide-react";

export const finishPlace = {
  OUTSIDE_MODAL: "Outside modal",
  SKIP_BUTTON: "Skip button",
  FINISH_BUTTON: "Finish button",
} as const;

export type FinishPlace = (typeof finishPlace)[keyof typeof finishPlace];

// Minimal interface that steps must implement for tracking
export interface StepTrackingData {
  stepNumber?: number;
  title: string;
  [key: string]: string | number | boolean | undefined; // Allow additional tracking properties
}

interface StepperModalProps<T> {
  open: boolean;
  onClose: () => void;
  steps: Array<T> | undefined;
  trackingPrefix: string; // e.g., "Trade Tutorial", "How It Works"
  maxWidth?: string;
  maxHeight?: string;
  contentHeight?: string;
  isLoading?: boolean;
  getStepTrackingData: (step: T) => StepTrackingData;
  renderStep: (
    step: T,
    index: number,
    isActive: boolean,
    currentIndex: number,
    onClose: (place: FinishPlace) => void,
  ) => ReactNode;
}

export const StepperModal = <T,>({
  open,
  onClose,
  steps,
  trackingPrefix,
  maxWidth = "800px",
  maxHeight = "700px",
  contentHeight = "520px",
  isLoading = false,
  getStepTrackingData,
  renderStep,
}: StepperModalProps<T>) => {
  const { isMobile } = useDevice();
  const { trackEvent } = useMetricsEvents();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset to first step when modal opens
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentStepIndex(0);
    }
  }

  const currentStep = steps?.[currentStepIndex];
  const currentStepTracking = currentStep ? getStepTrackingData(currentStep) : null;
  const isLastStep = currentStepIndex === (steps?.length ?? 0) - 1;
  const isFirstStep = currentStepIndex === 0;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    }

    if (currentStep) {
      const { stepNumber, title, ...other } = getStepTrackingData(currentStep);
      // Track when user clicks next in stepper modal
      trackEvent(METRICS_EVENTS.STEPPER_MODAL_NEXT, {
        trackingPrefix,
        currentStep: stepNumber,
        currentStepTitle: title,
        ...other,
      });
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex((prev) => prev - 1);
    }

    if (currentStep) {
      const { stepNumber, title, ...other } = getStepTrackingData(currentStep);
      // Track when user clicks back in stepper modal
      trackEvent(METRICS_EVENTS.STEPPER_MODAL_BACK, {
        trackingPrefix,
        currentStep: stepNumber,
        currentStepTitle: title,
        ...other,
      });
    }
  };

  const handleFinish = (place: FinishPlace) => {
    if (currentStep) {
      const { stepNumber, title, ...other } = getStepTrackingData(currentStep);
      // Track when user closes stepper modal
      trackEvent(METRICS_EVENTS.STEPPER_MODAL_CLOSE, {
        trackingPrefix,
        place,
        currentStep: stepNumber,
        currentStepTitle: title,
        ...other,
      });
    }

    onClose();
  };

  if (!currentStep && !isLoading) return null;

  return (
    <PortalModal
      open={open}
      onOpenChange={() => handleFinish(finishPlace.OUTSIDE_MODAL)}
      className={cn("!py-0 gap-0", isMobile ? "w-full h-[90vh]" : "w-auto")}
      style={{
        width: isMobile ? "100%" : maxWidth,
        maxWidth: isMobile ? "100%" : maxWidth,
        maxHeight: isMobile ? "90vh" : maxHeight,
      }}
    >
      <div
        className={cn(
          "p-4 md:p-6 pb-6 md:pb-8",
          "flex flex-col gap-2 md:gap-3 h-full",
          "focus:outline-none",
          isMobile && "rounded-b-none",
        )}
        style={{ maxHeight: isMobile ? "100%" : maxHeight }}
      >
        {isLoading ? (
          // Loading skeleton - matches actual content layout
          <>
            <div className="flex flex-col gap-1">
              <SkeletonElement className="h-4 w-32" />
              <SkeletonElement className="h-6 w-48" />
            </div>
            <div className="flex-1 min-h-0 flex flex-col gap-4">
              {/* Content area skeleton - use same height as actual content */}
              <div
                className="flex flex-col gap-4"
                style={isMobile ? { flex: "1 1 auto", minHeight: 0 } : { height: contentHeight }}
              >
                {/* Video/image placeholder */}
                <SkeletonElement className="w-full rounded-lg" style={{ height: "60%" }} />
                {/* Description text placeholders */}
                <div className="flex flex-col gap-2 px-2">
                  <SkeletonElement className="h-4 w-full" />
                  <SkeletonElement className="h-4 w-full" />
                  <SkeletonElement className="h-4 w-3/4" />
                </div>
              </div>
              {/* Step dots */}
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4].map((dotId) => (
                  <SkeletonElement key={dotId} className="w-2 h-2 rounded-full" />
                ))}
              </div>
              {/* Navigation buttons */}
              <div className="flex gap-4 justify-between">
                <SkeletonElement className="h-10 w-24 rounded-lg" />
                <div className="flex gap-3">
                  <SkeletonElement className="h-10 w-16 rounded-lg" />
                  <SkeletonElement className="h-10 w-24 rounded-lg" />
                </div>
              </div>
            </div>
          </>
        ) : (
          // Actual content
          <>
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-xs uppercase tracking-[0.2em] text-cede-text-secondary">{trackingPrefix}</p>
                {currentStepTracking?.title && (
                  <p className="text-base font-semibold text-cede-text-primary">{currentStepTracking.title}</p>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col gap-4">
              <div
                className="relative overflow-y-auto"
                style={isMobile ? { flex: "1 1 auto", minHeight: 0 } : { height: contentHeight }}
              >
                {steps?.map((step, index) =>
                  renderStep(step, index, index === currentStepIndex, currentStepIndex, handleFinish),
                )}
              </div>

              {/* Step Dots */}
              <div className="flex justify-center items-center gap-2 bg-transparent">
                {steps?.map((step, index) => (
                  <div
                    key={getStepTrackingData(step).stepNumber}
                    className={cn(
                      "w-2 h-2 rounded-full transition-colors duration-200",
                      index === currentStepIndex ? "bg-cede-primary" : "bg-cede-text-disabled",
                    )}
                  />
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-4 justify-between">
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={handleBack}
                  disabled={isFirstStep}
                  className="min-w-20"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {isLastStep ? (
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={() => handleFinish(finishPlace.FINISH_BUTTON)}
                    className="w-full max-w-20"
                  >
                    Finish
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={() => handleFinish(finishPlace.SKIP_BUTTON)}
                      className="w-fit"
                    >
                      Skip
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleNext} className="min-w-20">
                      Next
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </PortalModal>
  );
};
